import { useState, useEffect } from 'react';
import { config } from '../config/runtime';

export interface ApiAgent {
  id: string;
  name: string;
  displayName?: string;
  description?: string;
  isSystem?: boolean;
  defaultModelId?: string;
}

export interface Agent {
  id: string;
  agentId: string;
  name: string;
  type: string;
  status: 'online' | 'offline' | 'busy';
  capabilities?: string[];
  lastSeen?: string;
}

export function mapApiAgentToAgent(a: ApiAgent): Agent {
  return {
    id: a.id,
    agentId: a.name,
    name: a.displayName || a.name,
    type: a.isSystem ? 'system' : 'custom',
    status: 'online',
    capabilities: [a.description].filter((d): d is string => Boolean(d)),
  };
}

interface UseAgentsResult {
  agents: Agent[] | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetches agent definitions once from /api/agents (middleware-service).
 *
 * Pass `skip = true` (via `agentsProp !== undefined`) when the parent
 * already owns the fetch and provides agents via prop — this avoids a
 * duplicate network request from AgentList's internal fallback path.
 *
 * Note: SSE was removed. /api/projects/agents/stream does not exist.
 * Agent definitions are static at load time and fetched via REST only.
 */
export function useAgents(skip = false): UseAgentsResult {
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (skip) return;

    const fetchAgents = async () => {
      try {
        const response = await fetch(`${config.apiUrl}/api/agents`);
        if (!response.ok) {
          throw new Error('Failed to fetch agents');
        }
        const data = await response.json();
        const list: ApiAgent[] = Array.isArray(data) ? data : (data.agents ?? []);
        setAgents(list.map(mapApiAgentToAgent));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load agents');
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, [skip]);

  return { agents, loading, error };
}
